import { useCallback } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
    fetchCurrentEntitlements,
    invalidateEntitlementSnapshotCache,
    type EntitlementSnapshot,
} from '../entitlements';
import { useSession } from '../SessionContext';
import { supabase, toError } from '../supabase';
import {
    CURRENT_ENTITLEMENTS_QUERY_KEY,
    currentEntitlementsQueryKey,
} from '../currentEntitlementsQueryCache';

export { CURRENT_ENTITLEMENTS_QUERY_KEY, currentEntitlementsQueryKey };

async function getCurrentEntitlementSession() {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
        throw toError(error);
    }

    if (!session?.access_token || !session.user?.id) {
        throw new Error('You need to sign in to load entitlements.');
    }

    return session;
}

export async function refreshCurrentEntitlementsAuthoritatively(
    queryClient: QueryClient
): Promise<EntitlementSnapshot> {
    const session = await getCurrentEntitlementSession();
    const userId = session.user.id;
    const queryKey = currentEntitlementsQueryKey(userId);

    // Invalidate before cancelling so any slower request that was already in
    // flight is generation-stale and cannot repopulate either cache.
    invalidateEntitlementSnapshotCache(userId);
    await queryClient.cancelQueries({ queryKey, exact: true });

    const snapshot = await fetchCurrentEntitlements(session.access_token, {
        forceRefresh: true,
        userId,
    });
    queryClient.setQueryData(queryKey, snapshot);
    return snapshot;
}

export function useCurrentEntitlements(options: { enabled?: boolean } = {}) {
    const enabled = options.enabled ?? true;
    const { session } = useSession();
    const queryClient = useQueryClient();
    const queryKey = currentEntitlementsQueryKey(session?.user?.id);

    const query = useQuery({
        queryKey,
        enabled,
        staleTime: 0,
        refetchOnMount: 'always',
        queryFn: async () => {
            const currentSession = await getCurrentEntitlementSession();
            return fetchCurrentEntitlements(currentSession.access_token, {
                userId: currentSession.user.id,
            });
        },
    });

    const refreshAuthoritatively = useCallback(
        () => refreshCurrentEntitlementsAuthoritatively(queryClient),
        [queryClient]
    );

    return {
        ...query,
        refreshAuthoritatively,
    };
}
